import { useState } from 'react';
import type { PublicRoom } from '@planning-session/shared';
import { useSession, type SessionCredentials } from '../lib/useSession.js';
import { useKeyboardShortcuts } from '../lib/useKeyboardShortcuts.js';
import { Hand } from './Hand.js';
import { Notice } from './Notice.js';
import { Results } from './Results.js';
import { RoomHeader } from './RoomHeader.js';
import { SessionAside } from './SessionAside.js';
import { StoryBar } from './StoryBar.js';
import { Table } from './Table.js';
import { Button } from './ui/Button.js';
import styles from './Room.module.css';

export interface RoomProps {
  readonly credentials: SessionCredentials;
  onExit: () => void;
}

/**
 * The table's rail carries how far the round has got, so this row only says the
 * thing the rail cannot: what this particular person is waiting for.
 */
function waitingFor(room: PublicRoom, isHost: boolean): string {
  if (room.phase === 'revealed') return isHost ? '' : 'Waiting for the host to start the next round';
  if (isHost) return '';
  return room.allPlayersVoted ? 'Everyone has played — waiting for the host' : '';
}

export function Room({ credentials, onExit }: RoomProps) {
  const session = useSession(credentials);
  const [focusSignal, setFocusSignal] = useState(0);

  // Derived before the loading and error branches below, because the shortcut
  // hook has to be called on every render of this component.
  const room = session.room && session.room.kind === 'planning' ? session.room : null;
  const you = room?.you ?? null;
  const isHost = you?.isHost ?? false;
  const isPlayer = you?.role === 'player';
  const revealed = room?.phase === 'revealed';

  function leave() {
    session.leave();
    onExit();
  }

  function nextStory() {
    session.send({ type: 'nextRound', title: '' });
    // A queued round arrives already titled. Taking focus then would blank the
    // draft and commit that emptiness on blur, wiping the title the queue just
    // supplied.
    if (room && room.queue.length === 0) setFocusSignal((value) => value + 1);
  }

  useKeyboardShortcuts(
    {
      isPlayer,
      isHost,
      revealed,
      anyVotes: (room?.votedCount ?? 0) > 0,
      deckSize: room?.deck.cards.length ?? 0,
    },
    (action) => {
      switch (action.type) {
        case 'pick': {
          const value = room?.deck.cards[action.index];
          if (value !== undefined) session.send({ type: 'castVote', value });
          return;
        }
        case 'retract':
          session.send({ type: 'retractVote' });
          return;
        case 'reveal':
          session.send({ type: 'reveal' });
          return;
        case 'nextStory':
          nextStory();
          return;
      }
    },
  );

  if (session.fatal) {
    return (
      <div className={styles.centered}>
        <div className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Could not join</h1>
          <p className={styles.stateBody}>{session.fatal}</p>
          <Button variant="primary" onClick={onExit}>
            Back to the start
          </Button>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className={styles.centered}>
        <div className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Pulling up a chair…</h1>
          <p className={styles.stateBody}>Connecting to session {credentials.roomId}.</p>
        </div>
      </div>
    );
  }

  const waiting = waitingFor(room, isHost);

  return (
    <div className={styles.shell}>
      <RoomHeader brand="Planning Session" joinPath="room" roomId={room.id} you={room.you} status={session.status} onLeave={leave} />

      <div className={styles.main}>
        <section className={styles.stage}>
          <StoryBar
            title={room.round.title}
            canEdit={isHost}
            focusSignal={focusSignal}
            onCommit={(title) => session.send({ type: 'setTitle', title })}
          />

          <Table room={room} />

          <div className={styles.actions}>
            {isHost ? (
              revealed ? (
                <>
                  <Button variant="secondary" onClick={() => session.send({ type: 'reset' })}>
                    Vote again
                  </Button>
                  <Button variant="primary" size="large" onClick={nextStory}>
                    Next story
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="large"
                  disabled={room.votedCount === 0}
                  onClick={() => session.send({ type: 'reveal' })}
                >
                  {room.allPlayersVoted ? 'Reveal cards' : 'Reveal anyway'}
                </Button>
              )
            ) : (
              waiting && (
                <span className={styles.waiting}>
                  {!revealed && <span className={styles.pulse} aria-hidden="true" />}
                  {waiting}
                </span>
              )
            )}
          </div>

          {revealed && room.summary && <Results room={room} summary={room.summary} />}
        </section>

        <SessionAside
          room={room}
          isHost={isHost}
          isPlayer={isPlayer}
          onDeck={(deckId) => session.send({ type: 'setDeck', deckId })}
          onRole={(role) => session.send({ type: 'setRole', role })}
          onQueue={(titles) => session.send({ type: 'queueStories', titles })}
          onRemoveQueued={(storyId) => session.send({ type: 'removeQueuedStory', storyId })}
          onAutoReveal={(enabled) => session.send({ type: 'setAutoReveal', enabled })}
          onTransferHost={(participantId) => session.send({ type: 'transferHost', participantId })}
          onRemoveParticipant={(participantId) => session.send({ type: 'removeParticipant', participantId })}
        />
      </div>

      {isPlayer ? (
        <Hand
          deck={room.deck}
          selected={you?.vote ?? null}
          locked={revealed}
          onPick={(value) => session.send({ type: 'castVote', value })}
          onRetract={() => session.send({ type: 'retractVote' })}
        />
      ) : (
        <div />
      )}

      {session.notice && <Notice message={session.notice} onDismiss={session.dismissNotice} />}
    </div>
  );
}
